import React, { useState } from 'react';
import { X, Calendar, MapPin, Clock, Users, Home, Plane, ListOrdered, Edit3 } from 'lucide-react';
import { NativeDateInput, NativeTimeInput } from '../ui/NativeDateInput';
import { toast } from 'react-hot-toast';
import { gameService, type Game, type LineupSource } from '../../services/gameService';
import type { Sport } from '../../types/core';
import { getEventLabel } from '../../data/sportRulesets';
import { TennisMatchSetupFields, DEFAULT_TENNIS_MATCH_SETUP, type TennisMatchSetupValue } from './tennis/TennisMatchSetupFields';
import { getTennisMatchFormat } from '../../data/tennisMatchFormats';

interface CreateGameWizardProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onGameCreated: (game: Game) => void;
  /** Sport of the team creating the game — controls which fields are shown. */
  sport?: Sport;
}

export const CreateGameWizard: React.FC<CreateGameWizardProps> = ({
  isOpen,
  onClose,
  teamId,
  onGameCreated,
  sport,
}) => {
  // Only baseball-like sports have a batting order, so only they get the
  // lineup-source choice. Every other sport (tennis, basketball, soccer, …)
  // previously fell into this branch and was shown a batting field it has no
  // use for.
  const isBatting = sport === 'Baseball' || sport === 'Softball';
  const isTennis = sport === 'Tennis';
  const eventLabel = getEventLabel(sport);
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [opponentName, setOpponentName] = useState<string>('');
  const [homeAway, setHomeAway] = useState<'home' | 'away'>('home');
  const [location, setLocation] = useState<string>('');
  const [gameTime, setGameTime] = useState<string>('');
  const [lineupSource, setLineupSource] = useState<LineupSource>('depth_chart');
  const [tennisSetup, setTennisSetup] = useState<TennisMatchSetupValue>(DEFAULT_TENNIS_MATCH_SETUP);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date) {
      toast.error('Please select a date');
      return;
    }

    if (isTennis) {
      if (tennisSetup.playerIds.some((id) => !id)) {
        toast.error('Please select a player for every slot');
        return;
      }
      if (tennisSetup.opponentNames.some((name) => !name.trim())) {
        toast.error('Please enter an opponent name for every slot');
        return;
      }
    } else if (!opponentName.trim()) {
      toast.error('Please enter an opponent name');
      return;
    }

    setSaving(true);
    try {
      const game = await gameService.createGame({
        team_id: teamId,
        date,
        opponent_name: isTennis ? tennisSetup.opponentNames.map((n) => n.trim()).join(' / ') : opponentName.trim(),
        home_away: homeAway,
        location: location.trim() || undefined,
        game_time: gameTime || undefined,
        lineup_source: lineupSource,
        ...(isTennis
          ? {
              match_type: tennisSetup.matchType,
              match_format: { ...getTennisMatchFormat(tennisSetup.formatId), noAd: tennisSetup.noAd },
              ladder_slot: tennisSetup.ladderSlot || undefined,
              court: tennisSetup.court.trim() || undefined,
              opponent_school: tennisSetup.opponentSchool.trim() || undefined,
              match_state: 'scheduled' as const,
            }
          : {}),
      });

      if (isTennis) {
        await gameService.setMatchParticipants(game.id, tennisSetup.playerIds);
      }

      toast.success(`${eventLabel.singular} created successfully`);

      // Reset form
      setDate(new Date().toISOString().split('T')[0]);
      setOpponentName('');
      setHomeAway('home');
      setLocation('');
      setGameTime('');
      setLineupSource('depth_chart');
      setTennisSetup(DEFAULT_TENNIS_MATCH_SETUP);

      onGameCreated(game);
      onClose();
    } catch (error: any) {
      console.error(`Error creating ${eventLabel.singular.toLowerCase()}:`, error);
      toast.error(error?.message || `Failed to create ${eventLabel.singular.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create New {eventLabel.singular}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form
          id="create-game-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-6 space-y-6 pb-28"
        >
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline w-4 h-4 mr-2" />
              Date <span className="text-red-500">*</span>
            </label>
            <NativeDateInput
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          {/* Opponent Name — Tennis captures this per-side inside TennisMatchSetupFields instead */}
          {!isTennis && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline w-4 h-4 mr-2" />
                Opponent Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={opponentName}
                onChange={(e) => setOpponentName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter opponent team name"
                required
              />
            </div>
          )}

          {/* Home/Away */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Home className="inline w-4 h-4 mr-2" />
              Home/Away <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="homeAway"
                  value="home"
                  checked={homeAway === 'home'}
                  onChange={(e) => setHomeAway(e.target.value as 'home' | 'away')}
                  className="mr-2"
                />
                <Home className="w-4 h-4 mr-1" />
                Home
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="homeAway"
                  value="away"
                  checked={homeAway === 'away'}
                  onChange={(e) => setHomeAway(e.target.value as 'home' | 'away')}
                  className="mr-2"
                />
                <Plane className="w-4 h-4 mr-1" />
                Away
              </label>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="inline w-4 h-4 mr-2" />
              Location (Optional)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter game location or venue"
            />
          </div>

          {/* Game Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline w-4 h-4 mr-2" />
              Game Time (Optional)
            </label>
            <NativeTimeInput
              value={gameTime}
              onChange={(e) => setGameTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Lineup for this game — batting sports only */}
          {isBatting && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lineup for this game
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Use your default depth chart lineup, or set the batting order manually for this game.
              </p>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer flex-1 border rounded-lg p-3 border-gray-200 hover:bg-gray-50 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="lineupSource"
                    value="depth_chart"
                    checked={lineupSource === 'depth_chart'}
                    onChange={() => setLineupSource('depth_chart')}
                    className="mr-3"
                  />
                  <div>
                    <div className="flex items-center gap-2 font-medium text-gray-900">
                      <ListOrdered className="w-4 h-4 text-primary" />
                      Use default depth chart
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">Lineup from your Depth Chart (Lineup unit)</div>
                  </div>
                </label>
                <label className="flex items-center cursor-pointer flex-1 border rounded-lg p-3 border-gray-200 hover:bg-gray-50 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="lineupSource"
                    value="manual"
                    checked={lineupSource === 'manual'}
                    onChange={() => setLineupSource('manual')}
                    className="mr-3"
                  />
                  <div>
                    <div className="flex items-center gap-2 font-medium text-gray-900">
                      <Edit3 className="w-4 h-4 text-primary" />
                      Set lineup manually
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">Choose batting order on the game page</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Tennis match setup — type, player(s), ladder slot, opponent school, court, format */}
          {isTennis && (
            <TennisMatchSetupFields teamId={teamId} value={tennisSetup} onChange={setTennisSetup} />
          )}

        </form>

        {/* Actions (sticky footer so buttons aren't hidden behind mobile menus) */}
        <div
          className="border-t border-gray-200 bg-white px-6 py-4"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)' }}
        >
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-game-form"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating...' : `Create ${eventLabel.singular}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

